import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Button, Tooltip } from '@mui/material';
import { Add } from '@mui/icons-material';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from 'src/hooks/useDebounce';
import {
  loadInsurances,
  createInsurance,
  CREATE_INSURANCE
} from 'src/store/actions/insuranceActions';
import { selectInsuranceQueryOptions } from 'src/store/selectors/insuranceSelector';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import SearchBar from 'src/components/SearchBar';
import InsuranceDetailDialog from 'src/views/equipment/EquipmentDetail/InsuranceDetail/IsuranceDetailDialog';
import useStyles from './styles';

const Toolbar = ({ className, ...rest }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const queryOptions = useSelector(selectInsuranceQueryOptions);
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);
  const isCreating = useSelector(isLoadingSelector([CREATE_INSURANCE.action]));

  const debounceSearch = useDebounce(
    (id, queryOpts) => dispatch(loadInsurances({ ...queryOpts, id })),
    400
  );

  useEffect(() => {
    dispatch(loadInsurances());
  }, []);

  const handleSearch = useCallback(
    e => {
      const { value } = e.target;
      setSearchValue(value);
      debounceSearch(value, queryOptions);
    },
    [setSearchValue, debounceSearch, queryOptions]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleCreateInsurance = useCallback(
    values => {
      dispatch(createInsurance(values));
      handleClose();
    },
    [dispatch, handleClose]
  );

  return (
    <>
      <div className={clsx(classes.root, className)} {...rest}>
        <Breadcrumb doNotRedirectIndexList={[0]} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            style={{ fontWeight: '400' }}
            color="primary"
            variant="h1"
          >
            {t('INSURANCES_VIEW.TITLE')}
          </Typography>
          <div className={classes.actionsContainer}>
            <SearchBar
              handleSearch={handleSearch}
              searchValue={searchValue}
              placeholder={t('INSURANCES_VIEW.SEARCH_INSURANCE')}
              type="number"
            />
            <Tooltip title={t('INSURANCES_VIEW.ADD_NEW_INSURANCE')}>
              <Button
                className={clsx(classes.buttons, classes.outlined)}
                onClick={handleOpen}
                color="secondary"
                disabled={isCreating}
              >
                <Add className={classes.icon} />
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
      <InsuranceDetailDialog
        open={open}
        handleClose={handleClose}
        submit={handleCreateInsurance}
      />
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string
};

export default Toolbar;
